import React, { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import Papa from "papaparse";
import Icon from "@mdi/react";
import {
  mdiClose,
  mdiFileDocumentOutline,
  mdiAlertCircleOutline,
} from "@mdi/js";
import { fetchPost } from "../../../../service/api_client";
import {
  useToast,
  ToastType,
} from "../../../../context_providers/toast/toast_ctx";
import LoadingWidget from "../../../../context_providers/modal/loader";

type Props = {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
};

interface CsvRow {
  "invoice number": string;
  "lr number": string;
}

interface RowIssue {
  rowNum: number;
  reason: string;
}

type RowStatus = "updated" | "overwritten" | "skipped" | "failed";

interface ResultRow {
  row: number;
  invoice_number: string;
  lr_number: string;
  outward_id: string | null;
  type: string | null;
  resolved_partner: string | null;
  status: RowStatus;
  reason: string | null;
}

interface Summary {
  total: number;
  updated: number;
  overwritten: number;
  skipped: number;
  failed: number;
}

const MAX_ROWS = 2000;
const REQUIRED_HEADERS = ["invoice number", "lr number"];

const csvSafe = (value: string) =>
  /^[=+\-@]/.test(value) ? `'${value}` : value;

type Phase = "select" | "processing" | "done";

const statusStyle: Record<RowStatus, string> = {
  updated: "bg-green-100 text-green-700",
  overwritten: "bg-amber-100 text-amber-700",
  skipped: "bg-gray-100 text-gray-600",
  failed: "bg-red-100 text-red-700",
};

const SyncLrNumbersModal: React.FC<Props> = ({ open, onClose, onSuccess }) => {
  const { showToast } = useToast();
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [rows, setRows] = useState<
    { invoice_number: string; lr_number: string }[]
  >([]);
  const [headerIssues, setHeaderIssues] = useState<string[]>([]);
  const [parsing, setParsing] = useState(false);
  const [phase, setPhase] = useState<Phase>("select");
  const [uploadPercent, setUploadPercent] = useState(0);
  const [isDryRun, setIsDryRun] = useState(true);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [results, setResults] = useState<ResultRow[]>([]);

  const rowIssues: RowIssue[] = useMemo(() => {
    const issues: RowIssue[] = [];
    const seen = new Map<string, string>();
    rows.forEach((r, i) => {
      const rowNum = i + 2;
      const inv = r.invoice_number.trim();
      const lr = r.lr_number.trim();
      if (!inv) issues.push({ rowNum, reason: "Invoice Number is empty" });
      if (!lr) issues.push({ rowNum, reason: "LR Number is empty" });
      if (inv) {
        if (seen.has(inv) && seen.get(inv) !== lr) {
          issues.push({
            rowNum,
            reason: `Invoice '${inv}' repeated with a different LR`,
          });
        }
        seen.set(inv, lr);
      }
    });
    return issues;
  }, [rows]);

  if (!open) return null;

  const isOverLimit = rows.length > MAX_ROWS;
  const hasBlockers =
    headerIssues.length > 0 || rowIssues.length > 0 || isOverLimit;
  const canPreview =
    !!csvFile && rows.length > 0 && !hasBlockers && phase === "select";

  const resetResult = () => {
    setSummary(null);
    setResults([]);
  };

  const handleCsv = (file: File | null) => {
    resetResult();
    setRows([]);
    setHeaderIssues([]);
    setPhase("select");
    if (!file) {
      setCsvFile(null);
      return;
    }
    if (!/\.csv$/i.test(file.name) && file.type !== "text/csv") {
      showToast({ type: ToastType.error, text: "Please upload a .csv file" });
      return;
    }
    setCsvFile(file);
    setParsing(true);
    Papa.parse<CsvRow>(file, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (h) => h.replace(/^﻿/, "").trim().toLowerCase(),
      complete: (out) => {
        setParsing(false);
        const fields = out.meta.fields ?? [];
        const missing = REQUIRED_HEADERS.filter((h) => !fields.includes(h));
        const hdrIssues: string[] = [];
        if (missing.length) {
          hdrIssues.push(
            `Missing required columns: ${missing
              .map((m) =>
                m === "invoice number" ? "Invoice Number" : "LR Number",
              )
              .join(", ")}`,
          );
        }
        setHeaderIssues(hdrIssues);
        setRows(
          (out.data as CsvRow[]).map((r) => ({
            invoice_number: (r["invoice number"] ?? "").trim(),
            lr_number: (r["lr number"] ?? "").trim(),
          })),
        );
      },
      error: (err) => {
        setParsing(false);
        showToast({
          type: ToastType.error,
          text: `CSV parse error: ${err.message}`,
        });
      },
    });
  };

  const downloadTemplate = () => {
    const csv =
      "Invoice Number,LR Number\n" + "INV001,LR12345\n" + "INV002,LR12346\n";
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "sync_lr_numbers_template.csv";
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const downloadReport = () => {
    const failing = results.filter(
      (r) => r.status !== "updated" && r.status !== "skipped",
    );
    if (!failing.length) return;
    const rowsCsv = [
      "row,invoice_number,lr_number,st_id,status,reason",
      ...failing.map((e) =>
        [
          e.row,
          csvSafe(e.invoice_number),
          csvSafe(e.lr_number),
          csvSafe(e.outward_id ?? ""),
          e.status,
          JSON.stringify(e.reason ?? ""),
        ].join(","),
      ),
    ].join("\n");
    const blob = new Blob([rowsCsv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "sync_lr_errors.csv";
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const run = async (dryRun: boolean) => {
    if (!csvFile) return;
    setIsDryRun(dryRun);
    setPhase("processing");
    setUploadPercent(0);
    try {
      const fd = new FormData();
      fd.append("csv", csvFile, csvFile.name);
      const url = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/inventory/outward-inventory/sync-lr-numbers?dryRun=${dryRun}`;
      const res = await fetchPost(url, fd, undefined, {
        onUploadProgress: (e: any) => {
          if (e?.total) {
            setUploadPercent(Math.round((e.loaded / e.total) * 100));
          }
        },
      });
      if (!res?.success) {
        throw new Error(res?.error || "Sync LR failed");
      }
      setSummary(res.summary as Summary);
      setResults((res.results ?? []) as ResultRow[]);
      setPhase("done");
      if (!dryRun) {
        const writes = res.summary.updated + res.summary.overwritten;
        showToast({
          type:
            res.summary.failed > 0 || res.summary.overwritten > 0
              ? ToastType.warning
              : ToastType.success,
          text: `${writes}/${res.summary.total} LR numbers mapped`,
        });
        if (writes > 0 && onSuccess) onSuccess();
      }
    } catch (err: any) {
      showToast({ type: ToastType.error, text: err.message });
      setPhase("select");
    } finally {
      setUploadPercent(0);
    }
  };

  const eligible = summary ? summary.updated + summary.overwritten : 0;

  return createPortal(
    <div
      id="warehouse-outward-sync-lr-numbers-modal"
      className="fixed inset-0 z-50 flex items-start justify-center bg-black bg-opacity-50 p-4 overflow-y-auto"
      style={{ top: 0, left: 0, right: 0, bottom: 0 }}
    >
      <div
        className="relative w-full max-w-5xl my-auto flex flex-col rounded-xl bg-white shadow-xl overflow-hidden wh-modal-xs"
        style={{ maxHeight: "90vh" }}
      >
        <div className="flex items-center justify-between border-b px-6 py-4 shrink-0">
          <div>
            <h2 className="text-lg font-semibold">Sync LR Numbers</h2>
            <p className="text-xs text-gray-500">
              Map Delhivery LR numbers to dispatched shipments by invoice
              number. Max {MAX_ROWS} rows per upload.
            </p>
          </div>
          <button
            id="warehouse-outward-sync-lr-close-icon-btn"
            onClick={onClose}
            disabled={phase === "processing"}
            className={`rounded-full p-1 ${
              phase === "processing"
                ? "opacity-40 cursor-not-allowed"
                : "hover:bg-gray-100"
            }`}
          >
            <Icon path={mdiClose} size={0.9} />
          </button>
        </div>

        <div className="p-6 space-y-5 overflow-y-auto flex-1 min-h-0">
          {/* CSV picker */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-sm font-semibold">CSV file</label>
              <button
                id="warehouse-outward-sync-lr-download-template-btn"
                type="button"
                onClick={downloadTemplate}
                className="text-xs text-blue-600 hover:underline"
              >
                Download template
              </button>
            </div>
            <input
              id="warehouse-outward-sync-lr-csv-input"
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => handleCsv(e.target.files?.[0] ?? null)}
              className="block w-full text-sm border rounded-md px-3 py-2"
            />
            {csvFile && (
              <p className="mt-1 text-xs text-gray-600 flex items-center gap-1">
                <Icon path={mdiFileDocumentOutline} size={0.6} />
                {csvFile.name} — {rows.length} row(s)
              </p>
            )}
            {headerIssues.map((h) => (
              <p
                key={h}
                className="mt-1 text-xs text-red-600 flex items-center gap-1"
              >
                <Icon path={mdiAlertCircleOutline} size={0.6} />
                {h}
              </p>
            ))}
          </div>

          {/* In-file blockers */}
          {rows.length > 0 && rowIssues.length > 0 && (
            <div className="rounded-md border border-red-200 bg-red-50 p-3">
              <p className="text-sm font-semibold text-red-700">
                {rowIssues.length} blocker(s) — fix before continuing
              </p>
              <ul className="mt-1 max-h-40 overflow-y-auto space-y-0.5 text-sm text-red-600 list-disc list-inside">
                {rowIssues.map((iss, idx) => (
                  <li key={idx}>
                    Row {iss.rowNum}: {iss.reason}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {isOverLimit && (
            <p className="text-xs text-red-600">
              Over the {MAX_ROWS}-row limit — split the CSV.
            </p>
          )}

          {/* Summary + results */}
          {summary && (
            <div className="rounded-md border bg-gray-50 p-4 space-y-3">
              <div className="flex flex-wrap gap-4 text-sm">
                <span>
                  <b>Total</b>: {summary.total}
                </span>
                <span className="text-green-700">
                  <b>Updated</b>: {summary.updated}
                </span>
                <span className="text-amber-700">
                  <b>Overwritten</b>: {summary.overwritten}
                </span>
                <span className="text-gray-600">
                  <b>Skipped</b>: {summary.skipped}
                </span>
                <span className="text-red-700">
                  <b>Failed</b>: {summary.failed}
                </span>
                {isDryRun && (
                  <span className="text-blue-700 font-medium">
                    Preview only — nothing saved yet
                  </span>
                )}
              </div>

              <div
                className="overflow-auto border rounded-md bg-white"
                style={{ maxHeight: "45vh" }}
              >
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 border-b sticky top-0 z-10">
                    <tr>
                      <th className="p-2 text-left">#</th>
                      <th className="p-2 text-left">Invoice</th>
                      <th className="p-2 text-left">ST / Order ID</th>
                      <th className="p-2 text-left">LR Number</th>
                      <th className="p-2 text-left">Status</th>
                      <th className="p-2 text-left">Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.map((r, i) => (
                      <tr key={i} className="border-b last:border-b-0">
                        <td className="p-2">{r.row}</td>
                        <td className="p-2 font-mono">{r.invoice_number}</td>
                        <td className="p-2 font-mono">{r.outward_id ?? "—"}</td>
                        <td className="p-2 font-mono">{r.lr_number}</td>
                        <td className="p-2">
                          <span
                            className={`px-2 py-0.5 rounded-full text-[11px] font-medium capitalize ${statusStyle[r.status]}`}
                          >
                            {r.status}
                          </span>
                        </td>
                        <td className="p-2 text-gray-600">{r.reason ?? "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {results.some(
                (r) => r.status !== "updated" && r.status !== "skipped",
              ) && (
                <button
                  id="warehouse-outward-sync-lr-download-report-btn"
                  onClick={downloadReport}
                  className="text-xs text-blue-600 hover:underline"
                >
                  Download report (CSV)
                </button>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 border-t px-6 py-4 shrink-0">
          <button
            id="warehouse-outward-sync-lr-footer-close-btn"
            onClick={onClose}
            disabled={phase === "processing"}
            className={`h-10 px-4 rounded-md border border-gray-300 text-sm ${
              phase === "processing"
                ? "opacity-50 cursor-not-allowed"
                : "text-gray-700 hover:bg-gray-50"
            }`}
          >
            Close
          </button>

          {/* Preview (dry-run) before any write */}
          {(phase === "select" || (phase === "done" && isDryRun)) && (
            <button
              id="warehouse-outward-sync-lr-preview-btn"
              onClick={() => run(true)}
              disabled={!canPreview && phase !== "done"}
              className={`h-10 px-5 rounded-md text-sm font-medium ${
                canPreview || phase === "done"
                  ? "bg-blue-600 hover:bg-blue-700 text-white"
                  : "bg-gray-300 text-gray-500 cursor-not-allowed"
              }`}
            >
              {phase === "done"
                ? "Re-run preview"
                : `Preview ${rows.length} row(s)`}
            </button>
          )}

          {/* Commit — only after a preview that has eligible rows */}
          {phase === "done" && isDryRun && (
            <button
              id="warehouse-outward-sync-lr-commit-btn"
              onClick={() => run(false)}
              disabled={eligible === 0}
              className={`h-10 px-5 rounded-md text-sm font-medium ${
                eligible === 0
                  ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                  : "bg-green-600 hover:bg-green-700 text-white"
              }`}
            >
              Sync {eligible} LR number(s)
            </button>
          )}
        </div>

        {parsing && <LoadingWidget />}

        {phase === "processing" && (
          <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/95">
            <div className="text-center max-w-md px-6">
              {uploadPercent < 100 ? (
                <>
                  <p className="text-sm font-semibold text-gray-800 mb-3">
                    Uploading CSV…
                  </p>
                  <div className="w-80 h-4 bg-gray-200 border border-gray-300 rounded-full overflow-hidden mx-auto shadow-inner">
                    <div
                      className="h-full bg-blue-600 rounded-full transition-all duration-300"
                      style={{ width: `${Math.max(uploadPercent, 3)}%` }}
                    />
                  </div>
                  <p className="text-base font-semibold text-blue-700 mt-2">
                    {uploadPercent}%
                  </p>
                </>
              ) : (
                <>
                  <div className="mx-auto mb-3 h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent" />
                  <p className="text-sm font-semibold text-gray-800">
                    {isDryRun ? "Matching invoices…" : "Applying LR numbers…"}
                  </p>
                </>
              )}
              <p className="text-xs text-gray-500 mt-2">
                Please don’t close this tab.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
};

export default SyncLrNumbersModal;
